import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { camerasApi } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Monitor, Edit2, Check, X } from 'lucide-react';

export const CamerasDialog = ({ open, onOpenChange, eventId, cameras, onCamerasChange }) => {
  const [newCamera, setNewCamera] = useState({ name: '', notes: '' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCamera.name.trim()) return;

    setAdding(true);
    try {
      const res = await camerasApi.create(eventId, { name: newCamera.name, notes: newCamera.notes });
      onCamerasChange([...cameras, res.data]);
      setNewCamera({ name: '', notes: '' });
      toast.success('Pantalla agregada');
    } catch (error) {
      toast.error('Error al agregar pantalla');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (cameraId) => {
    try {
      const res = await camerasApi.update(eventId, cameraId, { name: editData.name, notes: editData.notes });
      onCamerasChange(cameras.map(c => c.id === cameraId ? res.data : c));
      setEditingId(null);
      toast.success('Pantalla actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (cameraId) => {
    if (!window.confirm('¿Eliminar esta pantalla? Se borrarán las imágenes de todas las fases.')) return;
    try {
      await camerasApi.delete(eventId, cameraId);
      onCamerasChange(cameras.filter(c => c.id !== cameraId));
      toast.success('Pantalla eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const startEdit = (camera) => {
    setEditingId(camera.id);
    setEditData({ name: camera.name, notes: camera.notes || '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Gestionar Pantallas
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6 overflow-y-auto flex-1 pr-1">
          {/* Status summary */}
          <div className="p-3 rounded-lg bg-muted flex items-center gap-3">
            <Monitor className="w-8 h-8 text-muted-foreground" />
            <div>
              <div className="text-2xl font-bold">{cameras.length}</div>
              <div className="text-xs text-muted-foreground">
                {cameras.length === 1 ? 'pantalla definida' : 'pantallas definidas'} · aparecen como columnas en cada fase
              </div>
            </div>
          </div>

          {/* Add new pantalla */}
          <form onSubmit={handleAdd} className="space-y-3 p-4 border border-dashed rounded-lg">
            <div className="text-sm font-medium text-muted-foreground">Agregar pantalla</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  placeholder="Ej: Pantalla Principal, Lateral Izq"
                  value={newCamera.name}
                  onChange={(e) => setNewCamera({ ...newCamera, name: e.target.value })}
                  data-testid="new-camera-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notas</Label>
                <Input
                  placeholder="Descripción opcional..."
                  value={newCamera.notes}
                  onChange={(e) => setNewCamera({ ...newCamera, notes: e.target.value })}
                />
              </div>
            </div>
            <Button type="submit" size="sm" disabled={adding || !newCamera.name.trim()} className="w-full" data-testid="add-camera-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar pantalla
            </Button>
          </form>

          {/* Pantallas list */}
          <ScrollArea className="h-[280px]">
            {cameras.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Monitor className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay pantallas definidas</p>
                <p className="text-xs mt-1">Agrega pantallas para crear columnas dinámicas en las fases</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cameras.map((camera, idx) => (
                  <div
                    key={camera.id}
                    className="p-3 rounded-lg border border-border"
                    data-testid={`camera-item-${camera.id}`}
                  >
                    {editingId === camera.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            placeholder="Nombre"
                            className="h-8"
                          />
                          <Input
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            placeholder="Notas"
                            className="h-8"
                          />
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => handleUpdate(camera.id)}>
                            <Check className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{camera.name}</div>
                          {camera.notes && (
                            <div className="text-xs text-muted-foreground">{camera.notes}</div>
                          )}
                        </div>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(camera)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(camera.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CamerasDialog;
